import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { FileUploadZone } from './upload/FileUploadZone';
import { ValidationErrors } from './upload/ValidationErrors';
import { WarningMessage } from './upload/WarningMessage';
import { useAccommodationStore } from '../../stores/accommodationStore';
import { usePackagesStore } from '../../stores/packagesStore';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { Room } from '../../types/accommodation';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type UniqueHotel = {
  id: string;
  name: string;
};

const parseExcelFile = async (file: File): Promise<Room[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error("Failed to read file data"));
          return;
        }
        
        const workbook = XLSX.read(data, { type: 'binary' });
        
        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          reject(new Error("Excel file does not contain any sheets"));
          return;
        }
        
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        if (!jsonData || jsonData.length === 0) {
          reject(new Error("No data found in Excel file"));
          return;
        }

        // Convert Excel data to Room objects
        const rooms: Room[] = [];
        
        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i] as any;
          const rowIndex = i + 2; // +2 because Excel is 1-based and we have a header row
          
          const roomType = row['room type']?.toLowerCase();
          // Store room number as string to allow alphanumeric values
          const roomNumber = String(row['room number']);

          // Validate room number (just check if it exists)
          if (!roomNumber) {
            reject(new Error(`Missing room number at row ${rowIndex}`));
            return;
          }

          // Validate room type
          if (!roomType || !['double', 'triple', 'quadruple'].includes(roomType)) {
            reject(new Error(`Invalid room type at row ${rowIndex}: ${roomType || 'empty'}`));
            return;
          }

          // Map room type to short form
          const typeMap = {
            'double': 'd',
            'triple': 't',
            'quadruple': 'q'
          };

          // Calculate capacity based on room type
          const capacityMap = {
            'd': 2,
            't': 3,
            'q': 4
          };

          const type = typeMap[roomType as keyof typeof typeMap];
          const capacity = capacityMap[type];

          rooms.push({
            n: roomNumber,
            t: type,
            c: capacity,
            o: 0,
            b: Array(capacity).fill(null).map(() => ({ a: [] }))
          });
        }

        // Check for duplicate room numbers
        const roomNumbers = new Set();
        for (const room of rooms) {
          if (roomNumbers.has(room.n)) {
            reject(new Error(`Duplicate room number: ${room.n}`));
            return;
          }
          roomNumbers.add(room.n);
        }

        resolve(rooms);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        reject(error || new Error('Failed to parse Excel file'));
      }
    };

    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      reject(new Error('Failed to read the file'));
    };

    try {
      reader.readAsBinaryString(file);
    } catch (error) {
      console.error('Error reading file:', error);
      reject(new Error('Failed to read the file: ' + (error as Error).message));
    }
  });
};

export const AddTableModal = ({ isOpen, onClose }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [selectedHotelId, setSelectedHotelId] = useState('');
  const [tableName, setTableName] = useState('');
  const [uniqueHotels, setUniqueHotels] = useState<UniqueHotel[]>([]);
  
  const { createTable, tables } = useAccommodationStore();
  const { packages, fetchPackages } = usePackagesStore();

  // Fetch packages when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPackages();
      
      // Reset form state when modal opens
      setFile(null);
      setValidationErrors([]);
      setSelectedHotelId('');
      setTableName('');
      setIsLoading(false);
    }
  }, [isOpen, fetchPackages]);

  // Extract unique hotels from packages and filter out used ones
  useEffect(() => {
    const hotelsSet = new Set<string>();
    const hotels: UniqueHotel[] = [];
    const usedHotels = new Set(Object.values(tables).map(table => table.hn));

    Object.values(packages).forEach(pkg => {
      // Add main Makkah hotel if not already used
      if (pkg.mainMakkahHotel && !hotelsSet.has(pkg.mainMakkahHotel) && !usedHotels.has(pkg.mainMakkahHotel)) {
        hotelsSet.add(pkg.mainMakkahHotel);
        hotels.push({
          id: pkg.mainMakkahHotel,
          name: pkg.mainMakkahHotel
        });
      }

      // Add shifting Makkah hotel if not already used
      if (pkg.shiftingMakkahHotel && !hotelsSet.has(pkg.shiftingMakkahHotel) && !usedHotels.has(pkg.shiftingMakkahHotel)) {
        hotelsSet.add(pkg.shiftingMakkahHotel);
        hotels.push({
          id: pkg.shiftingMakkahHotel,
          name: pkg.shiftingMakkahHotel
        });
      }

      // Add Madinah hotel if not already used
      if (pkg.madinahHotel && !hotelsSet.has(pkg.madinahHotel) && !usedHotels.has(pkg.madinahHotel)) {
        hotelsSet.add(pkg.madinahHotel);
        hotels.push({
          id: pkg.madinahHotel,
          name: pkg.madinahHotel
        });
      }
    });

    // Sort hotels alphabetically
    setUniqueHotels(hotels.sort((a, b) => a.name.localeCompare(b.name)));
  }, [packages, tables]);

  const handleDownloadTemplate = () => {
    const template = [
      {
        'room number': '101',
        'room type': 'double'
      },
      {
        'room number': '102',
        'room type': 'triple'
      },
      {
        'room number': '103',
        'room type': 'quadruple'
      },
      {
        'room number': 'DV 104',
        'room type': 'double'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');

    XLSX.writeFile(wb, 'accommodation-template.xlsx');
  };

  const handleSubmit = async () => {
    if (!file || !selectedHotelId || !tableName) {
      toast.error('جميع الحقول مطلوبة');
      return;
    }

    setIsLoading(true);
    setValidationErrors([]);
    
    try {
      const selectedHotel = uniqueHotels.find(h => h.id === selectedHotelId);
      if (!selectedHotel) {
        throw new Error('الفندق غير موجود');
      }

      await createTable(selectedHotelId, selectedHotel.name, tableName, file);
      toast.success('تم إنشاء جدول التسكين بنجاح');
      onClose();
    } catch (error: any) {
      console.error('Error creating table:', error);
      setValidationErrors([error.message || 'فشل في إنشاء جدول التسكين']);
      toast.error(error.message || 'فشل في إنشاء جدول التسكين');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="إضافة جدول تسكين">
      <div className="space-y-6">
        {/* Hotel Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            الفندق
            <span className="text-red-500 mr-1">*</span>
          </label>
          <select
            value={selectedHotelId}
            onChange={(e) => setSelectedHotelId(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="">اختر الفندق</option>
            {uniqueHotels.map(hotel => (
              <option key={hotel.id} value={hotel.id}>{hotel.name}</option>
            ))}
          </select>
        </div>

        {/* Table Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            اسم الجدول
            <span className="text-red-500 mr-1">*</span>
          </label>
          <input
            type="text"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        {/* Template Download Button */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>تحميل نموذج Excel</span>
        </button>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ملف Excel
            <span className="text-red-500 mr-1">*</span>
          </label>
          <FileUploadZone
            file={file}
            onFileSelect={setFile}
            onFileRemove={() => {
              setFile(null);
              setValidationErrors([]);
            }}
          />
        </div>

        <ValidationErrors errors={validationErrors} />
        <WarningMessage />

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            إلغاء
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !file || !selectedHotelId || !tableName}
            className={`px-6 py-2 rounded-lg text-white
              ${!file || isLoading || !selectedHotelId || !tableName
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {isLoading ? 'جاري الإنشاء...' : 'إنشاء'}
          </button>
        </div>
      </div>
    </Modal>
  );
};